import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Headline from 'components/Headline/styles';
import MoreActionsMenu from 'components/MoreActionsMenu';
import IconActiveAlert from 'assets/inviteABite/iab-active-alert.svg';
import IconActiveBanner from 'assets/inviteABite/iab-active-banner.svg';
import IconActiveCustomers from 'assets/inviteABite/iab-active-customers.svg';
import IconWarning from 'assets/inviteABite/iab-warning.svg';
import RevenueCard from 'routes/App/inviteABite/ActiveState/RevenueCard';
import ReferralFunnel from 'routes/App/inviteABite/ActiveState/ReferralFunnel';
import {
  IABActiveBanner,
  IABActiveBannerIcon,
  IABActiveBannerText,
  IABActiveBannerTitle,
  IABActiveStateHeader,
  IABBenchmarkBar,
  IABBenchmarkSegment,
  IABDiscountAlert,
  IABDiscountAlertButton,
  IABDiscountAlertIcon,
  IABDiscountAlertText,
  IABDiscountAlertTextBlock,
  IABDiscountAlertTitle,
  IABDiscountAlertWrapper,
  IABDiscountCard,
  IABDiscountColumn,
  IABDiscountCompare,
  IABDiscountLabel,
  IABDiscountTierBadge,
  IABDiscountTierIcon,
  IABDiscountValue,
  IABMetricCard,
  IABMetricIcon,
  IABMetricContentRow,
  IABMetricLabel,
  IABMetricTitleRow,
  IABMetricSubtext,
  IABMetricTextBlock,
  IABMetricValue,
  IABMetricsColumn,
  IABMetricsFunnelGrid
} from 'routes/App/inviteABite/ActiveState/styles';

const BOOST_URL = 'https://share.hsforms.com/1SjnI8IRNQSSI8Cin6tv8Zw21htm';

const getTier = (percent) => {
  if (percent >= 60) return {label: 'Top performer', className: 'top'};
  if (percent >= 40) return {label: 'Best performing', className: 'best'};
  return {label: 'Below average', className: 'below'};
};

const ActiveState = ({data}) => {
  const rewardPercent = Number(data?.rewardPercentage ?? 0);
  const newCustomersCount = data?.newCustomersReferredCount ?? 0;
  const revenueFromReferrals = data?.revenueFromReferrals ?? 0;
  const referralFunnel = data?.referralFunnel ?? null;
  const hasFunnelData =
    referralFunnel &&
    (referralFunnel.shared > 0 ||
      referralFunnel.viewed > 0 ||
      referralFunnel.firstOrder > 0);

  const tier = getTier(rewardPercent);

  return (
    <>
      <IABActiveStateHeader className='AlwaysFlex'>
        <Headline className='mTop5'>Invite a Bite</Headline>
        <MoreActionsMenu
          options={[
            {
              label: 'Change percentage',
              onClick: () =>
                window.open(BOOST_URL, '_blank', 'noopener,noreferrer')
            }
          ]}
        />
      </IABActiveStateHeader>
      <IABActiveBanner>
        <IABActiveBannerIcon src={IconActiveBanner} alt='' />
        <div>
          <IABActiveBannerTitle>Invite a bite is active</IABActiveBannerTitle>
          <IABActiveBannerText>
            Your customers receive a unique referral link after every order.
            Reward set at <strong>{rewardPercent}%</strong> for both sides.
          </IABActiveBannerText>
        </div>
      </IABActiveBanner>

      <IABDiscountCard>
        <IABDiscountColumn>
          <IABDiscountValue>{rewardPercent}%</IABDiscountValue>
          <div>
            <IABDiscountLabel>Your referral discount</IABDiscountLabel>
            <IABDiscountTierBadge className={tier.className}>
              {tier.className === 'below' && (
                <IABDiscountTierIcon src={IconWarning} alt='' />
              )}
              {tier.label}
            </IABDiscountTierBadge>
          </div>
        </IABDiscountColumn>

        <IABDiscountCompare>
          <IABDiscountLabel>How your discount compares</IABDiscountLabel>
          <IABBenchmarkBar>
            <IABBenchmarkSegment
              className={cn('below', {active: tier.className === 'below'})}
            >
              Under 40%
            </IABBenchmarkSegment>
            <IABBenchmarkSegment
              className={cn('best', {active: tier.className === 'best'})}
            >
              40–60%
            </IABBenchmarkSegment>
            <IABBenchmarkSegment
              className={cn('top', {active: tier.className === 'top'})}
            >
              60%+
            </IABBenchmarkSegment>
          </IABBenchmarkBar>
        </IABDiscountCompare>
        <IABDiscountAlertWrapper>
          <IABDiscountAlert>
            <IABDiscountAlertIcon src={IconActiveAlert} alt='' />
            <IABDiscountAlertTextBlock>
              <IABDiscountAlertTitle>
                Increase your discount to get more referrals
              </IABDiscountAlertTitle>
              <IABDiscountAlertText>
                Restaurants offering 40%+ off first orders usually get better
                results. A stronger offer makes referrals easier to share, and
                you only pay when a new customer orders.
              </IABDiscountAlertText>
            </IABDiscountAlertTextBlock>
          </IABDiscountAlert>
          <IABDiscountAlertButton
            className='ButtonMedium'
            onClick={() =>
              window.open(BOOST_URL, '_blank', 'noopener,noreferrer')
            }
          >
            <span>Boost referrals</span>
          </IABDiscountAlertButton>
        </IABDiscountAlertWrapper>
      </IABDiscountCard>

      <IABMetricsFunnelGrid>
        <IABMetricsColumn>
          <IABMetricCard>
            <IABMetricTitleRow>
              <IABMetricIcon src={IconActiveCustomers} alt='' />
              <IABMetricTextBlock>
                <IABMetricLabel>New customers referred</IABMetricLabel>
              </IABMetricTextBlock>
            </IABMetricTitleRow>
            <IABMetricContentRow>
              <IABMetricValue>{newCustomersCount}</IABMetricValue>
              <IABMetricSubtext>
                First-time orders from referrals
              </IABMetricSubtext>
            </IABMetricContentRow>
          </IABMetricCard>
          <RevenueCard revenue={revenueFromReferrals} />
        </IABMetricsColumn>
        {hasFunnelData && <ReferralFunnel funnel={referralFunnel} />}
      </IABMetricsFunnelGrid>
    </>
  );
};

ActiveState.propTypes = {
  data: PropTypes.shape({
    isEnabled: PropTypes.bool,
    rewardPercentage: PropTypes.number,
    newCustomersReferredCount: PropTypes.number,
    revenueFromReferrals: PropTypes.number,
    referralFunnel: PropTypes.shape({
      shared: PropTypes.number,
      viewed: PropTypes.number,
      firstOrder: PropTypes.number
    })
  })
};

ActiveState.defaultProps = {
  data: null
};

export default ActiveState;
